"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { AnimatePresence, motion } from "framer-motion";
import Link from "next/link";
import { ArrowRight, ChevronDown } from "lucide-react";
import MegaMenuColumn from "@/components/nav/MegaMenuColumn";
import { EASE } from "@/lib/motion";
import {
  RATGEBER_PATH,
  ratgeberActivePaths,
  TRACKER_ARTICLE_PATH,
  type RatgeberColumn as RatgeberColumnData,
} from "@/lib/ratgeber";
import { cn } from "@/lib/utils";

/**
 * Desktop-Mega-Menü „Ratgeber": öffnet per Hover oder Klick,
 * schließt bei Außenklick, Esc, Fokusverlust und Routenwechsel.
 *
 * `ratgeberColumns` kommt als Prop von der Server-Komponente `Navbar` (siehe
 * dort) statt aus einem direkten Import von `lib/ratgeber-columns` – dieses
 * Modul zieht den kompletten Blog-Datensatz (~60 Artikel) nach, der so nie
 * ins Client-Bundle wandert, nur weil dieses "use client"-Menü ihn bräuchte.
 */
export default function MegaMenu({
  ratgeberColumns,
}: {
  ratgeberColumns: RatgeberColumnData[];
}) {
  const [open, setOpen] = useState(false);
  const pathname = usePathname();
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Der Aufrück-Tracker-Artikel hat einen eigenen, dominanten Menüpunkt –
  // dort soll „Ratgeber" nicht zusätzlich aktiv markiert werden.
  const active =
    pathname !== TRACKER_ARTICLE_PATH &&
    ratgeberActivePaths.some((path) => pathname.startsWith(path));

  const cancelClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), 150);
  }, [cancelClose]);

  const close = useCallback(() => {
    cancelClose();
    setOpen(false);
  }, [cancelClose]);

  // Bei Routenwechsel schließen
  useEffect(() => close(), [pathname, close]);

  // Esc + Außenklick
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        close();
        buttonRef.current?.focus();
      }
    };
    const onPointerDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close();
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [open, close]);

  useEffect(() => cancelClose, [cancelClose]);

  return (
    // Bewusst ohne position:relative – das Panel verankert sich am sticky
    // <header> (nächster positionierter Vorfahre) und bleibt so im Viewport.
    <div
      ref={rootRef}
      onMouseEnter={() => {
        cancelClose();
        setOpen(true);
      }}
      onMouseLeave={scheduleClose}
      onBlur={(e) => {
        // Schließen, sobald der Fokus das Menü komplett verlässt
        if (rootRef.current && !rootRef.current.contains(e.relatedTarget as Node)) {
          close();
        }
      }}
    >
      <button
        ref={buttonRef}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
        aria-controls="ratgeber-mega-menu"
        className={cn(
          "relative inline-flex items-center gap-2 text-sm transition-colors duration-200 cursor-pointer",
          active || open
            ? "text-ink font-semibold"
            : "text-ink-soft font-medium hover:text-ink"
        )}
      >
        <span
          className={cn(
            "w-1.5 h-1.5 bg-accent transition-opacity duration-200",
            active ? "opacity-100" : "opacity-0"
          )}
          aria-hidden="true"
        />
        Ratgeber
        <ChevronDown
          size={14}
          className={cn("transition-transform duration-300", open && "rotate-180")}
          aria-hidden="true"
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            id="ratgeber-mega-menu"
            initial={{ opacity: 0, y: -8, x: "-50%" }}
            animate={{ opacity: 1, y: 0, x: "-50%" }}
            exit={{ opacity: 0, y: -8, x: "-50%" }}
            transition={{ duration: 0.22, ease: EASE }}
            className="absolute left-1/2 top-full pt-3 w-[min(58rem,calc(100vw-2rem))] z-50"
          >
            <div className="card-flat bg-white shadow-xl">
              <div className="py-7 lg:py-8 grid grid-cols-3 divide-x divide-line">
                {ratgeberColumns.map((column) => (
                  <div key={column.id} className="px-7 lg:px-8 flex min-w-0">
                    <MegaMenuColumn column={column} onNavigate={close} />
                  </div>
                ))}
              </div>
              <div className="border-t border-line px-7 lg:px-8 py-4">
                <Link
                  href={RATGEBER_PATH}
                  onClick={close}
                  className="inline-flex items-center gap-1.5 text-sm font-bold text-accent hover:text-accent-deep transition-colors"
                >
                  Alle Ratgeber im Überblick
                  <ArrowRight size={14} aria-hidden="true" />
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
